import * as React from 'react';
import { lazy, Suspense, useCallback } from 'react';
import { Route, Switch, Redirect } from 'react-router-dom';

import { useReactiveVar } from '@apollo/client';

import { userStateVar } from 'core';
import { UserRole } from 'types';

// Small pre-bootstrap pages stay static (they also render outside Suspense).
import { ErrorPage } from 'pages/ErrorPage';
import { Splash } from 'pages/Splash';

import { PATHS, PATHS_PARAMS } from './config';

const RELOAD_FLAG = 'lazy-chunk-reloaded';

/**
 * Route-level code splitting (spec 0008 round 2): every section is its own
 * chunk, so a finance-only user no longer downloads the communicator,
 * chart.js, the DnD board, etc. before first paint.
 *
 * Deploys wipe old chunks from the bucket (`aws s3 rm` / DELETE_FLAG in
 * bitbucket-pipelines.yml), so a client still holding a pre-deploy
 * index.html would crash on its first lazy navigation — one forced reload
 * picks up the fresh manifest; the sessionStorage flag prevents a reload
 * loop when the chunk is genuinely broken.
 *
 * IMPORTANT: import page FOLDERS directly ('pages/Students'), never the
 * `pages` barrel — one barrel import would pull every page back into the
 * main chunk (package.json has no sideEffects field, webpack 4 keeps it).
 */
const lazyPage = <T extends React.ComponentType<any>>(
  factory: () => Promise<{ default: T }>,
) =>
  lazy(() =>
    factory()
      .then((module) => {
        window.sessionStorage.removeItem(RELOAD_FLAG);
        return module;
      })
      .catch((error) => {
        if (!window.sessionStorage.getItem(RELOAD_FLAG)) {
          window.sessionStorage.setItem(RELOAD_FLAG, '1');
          window.location.reload();
          // Keep the Suspense fallback up while the reload happens.
          return new Promise<{ default: T }>(() => {});
        }
        throw error;
      }),
  );

const Debtors = lazyPage(() =>
  import('pages/Debtors').then((m) => ({ default: m.Debtors })),
);
const McpAccess = lazyPage(() =>
  import('pages/McpAccess').then((m) => ({ default: m.McpAccess })),
);
const UsersSettings = lazyPage(() =>
  import('pages/UsersSettings').then((m) => ({ default: m.UsersSettings })),
);
const Students = lazyPage(() =>
  import('pages/Students').then((m) => ({ default: m.Students })),
);
const PlatformSettings = lazyPage(() =>
  import('pages/PlatformSettings').then((m) => ({
    default: m.PlatformSettings,
  })),
);
const Journal = lazyPage(() =>
  import('pages/Journal').then((m) => ({ default: m.Journal })),
);
const Finances = lazyPage(() =>
  import('pages/Finances').then((m) => ({ default: m.Finances })),
);
const TeachersOption = lazyPage(() =>
  import('pages/TeachersOption').then((m) => ({ default: m.TeachersOption })),
);
const Performance = lazyPage(() =>
  import('pages/Performance').then((m) => ({ default: m.Performance })),
);
const ZoomRecordings = lazyPage(() =>
  import('pages/ZoomRecordings').then((m) => ({ default: m.ZoomRecordings })),
);
const PandaDocAnalytics = lazyPage(() =>
  import('pages/PandaDocAnalytics').then((m) => ({
    default: m.PandaDocAnalytics,
  })),
);
const PandaDocProvisioning = lazyPage(() =>
  import('pages/PandaDocProvisioning').then((m) => ({
    default: m.PandaDocProvisioning,
  })),
);
const FakturowniaInvoices = lazyPage(() =>
  import('pages/FakturowniaInvoices').then((m) => ({
    default: m.FakturowniaInvoices,
  })),
);
const FakturowniaInvoiceDetail = lazyPage(() =>
  import('pages/FakturowniaInvoices').then((m) => ({
    default: m.FakturowniaInvoiceDetail,
  })),
);
const FakturowniaExpenses = lazyPage(() =>
  import('pages/FakturowniaExpenses').then((m) => ({
    default: m.FakturowniaExpenses,
  })),
);
const FinanceDashboard = lazyPage(() =>
  import('pages/FinanceDashboard').then((m) => ({
    default: m.FinanceDashboard,
  })),
);
const CashFlowReport = lazyPage(() =>
  import('pages/CashFlowReport').then((m) => ({ default: m.CashFlowReport })),
);

const PaymentCalendar = lazyPage(() =>
  import('pages/PaymentCalendar').then((m) => ({
    default: m.PaymentCalendar,
  })),
);
const StudentDetail = lazyPage(() =>
  import('pages/StudentDetail').then((m) => ({ default: m.StudentDetail })),
);
const GroupDetail = lazyPage(() =>
  import('pages/GroupDetail').then((m) => ({ default: m.GroupDetail })),
);
const TeacherDetail = lazyPage(() =>
  import('pages/TeacherDetail').then((m) => ({ default: m.TeacherDetail })),
);
const AccountManagerDashboard = lazyPage(() =>
  import('pages/AccountManagerDashboard').then((m) => ({
    default: m.AccountManagerDashboard,
  })),
);
const Communicator = lazyPage(() =>
  import('pages/Communicator').then((m) => ({ default: m.Communicator })),
);
const LessonWhiteboard = lazyPage(() =>
  import('pages/LessonWhiteboard').then((m) => ({
    default: m.LessonWhiteboardPage,
  })),
);
const Surveys = lazyPage(() =>
  import('pages/Surveys').then((m) => ({ default: m.Surveys })),
);
const SurveyBuilder = lazyPage(() =>
  import('pages/SurveyBuilder').then((m) => ({ default: m.SurveyBuilder })),
);
const SurveyResults = lazyPage(() =>
  import('pages/SurveyResults').then((m) => ({ default: m.SurveyResults })),
);
// Spec 0018 career centre. Import the page FOLDER, never the `pages` barrel —
// a barrel import drags every page into the main chunk.
const CareerCenter = lazyPage(() =>
  import('pages/CareerCenter').then((m) => ({ default: m.CareerCenter })),
);
const Dofinansowanie = lazyPage(() =>
  import('pages/Dofinansowanie').then((m) => ({ default: m.Dofinansowanie })),
);

const ROUTES = {
  // Must precede `pandadoc` and `debtors`: `/debtors/pandadoc/provisioning` is
  // a sub-path of both, and neither of those routes is `exact`.
  pandadocProvisioning: (
    <Route
      path={PATHS.debtorsPandadocProvisioning}
      component={PandaDocProvisioning}
      key={PATHS.debtorsPandadocProvisioning}
    />
  ),
  // Must precede `debtors`: `/debtors/pandadoc` is a sub-path of `/debtors`
  // and the `debtors` route is not `exact`.
  pandadoc: (
    <Route
      path={PATHS.debtorsPandadoc}
      component={PandaDocAnalytics}
      key={PATHS.debtorsPandadoc}
    />
  ),
  debtors: (
    <Route path={PATHS.debtors} component={Debtors} key={PATHS.debtors} />
  ),
  mcpAccess: (
    <Route path={PATHS.mcpAccess} component={McpAccess} key={PATHS.mcpAccess} />
  ),
  debtorsContributions: (
    <Route
      path={`${PATHS.contributions}/${PATHS_PARAMS.contributionNum}`}
      component={Debtors}
      key={`${PATHS.contributions}/${PATHS_PARAMS.contributionNum}`}
    />
  ),
  // Detail must precede list — list has no `exact` on the prefix branch.
  studentDetail: (
    <Route
      path={PATHS.studentDetail}
      component={StudentDetail}
      key={PATHS.studentDetail}
    />
  ),
  students: (
    <Route
      path={`${PATHS.students}`}
      exact
      component={Students}
      key={`${PATHS.students}`}
    />
  ),
  groupDetail: (
    <Route
      path={PATHS.groupDetail}
      component={GroupDetail}
      key={PATHS.groupDetail}
    />
  ),
  // Must precede teachers branch — `/teachers/:id/details` shadows the list.
  teacherDetail: (
    <Route
      path={PATHS.teacherDetail}
      component={TeacherDetail}
      key={PATHS.teacherDetail}
    />
  ),
  users: (
    <Route path={PATHS.users} component={UsersSettings} key={PATHS.users} />
  ),
  settings: (
    <Route
      path={PATHS.settings}
      component={PlatformSettings}
      key={PATHS.settings}
    />
  ),
  journal: (
    <Route
      path={`${PATHS.journal}/:journalId`}
      component={Journal}
      key={PATHS.journal}
    />
  ),
  financeDashboard: (
    <Route
      path={PATHS.financesDashboard}
      component={FinanceDashboard}
      key={PATHS.financesDashboard}
    />
  ),
  financePaymentCalendar: (
    <Route
      path={PATHS.financesPaymentCalendar}
      component={PaymentCalendar}
      key={PATHS.financesPaymentCalendar}
    />
  ),
  financeCashFlow: (
    <Route
      path={PATHS.financesCashFlow}
      component={CashFlowReport}
      key={PATHS.financesCashFlow}
    />
  ),
  // Detail must precede list — the list route is not `exact` and would
  // otherwise swallow `/finances/invoices/:id`.
  fakturowniaInvoice: (
    <Route
      path={PATHS.financesInvoice}
      component={FakturowniaInvoiceDetail}
      key={PATHS.financesInvoice}
    />
  ),
  fakturowniaInvoices: (
    <Route
      path={PATHS.financesInvoices}
      component={FakturowniaInvoices}
      key={PATHS.financesInvoices}
    />
  ),
  fakturowniaExpenses: (
    <Route
      path={PATHS.financesExpenses}
      component={FakturowniaExpenses}
      key={PATHS.financesExpenses}
    />
  ),
  // Must precede `/finances/invoices` and the planning page —
  // `/finances` matches first because it's the bare prefix.
  finances: (
    <Route
      path={PATHS.finances}
      exact
      component={Finances}
      key={PATHS.finances}
    />
  ),
  teachers: (
    <Route
      path={PATHS.teachers}
      component={TeachersOption}
      key={PATHS.teachers}
    />
  ),
  performance: (
    <Route
      path={PATHS.performance}
      component={Performance}
      key={PATHS.performance}
    />
  ),
  zoom: <Route path={PATHS.zoom} component={ZoomRecordings} key={PATHS.zoom} />,
  // Spec 0010: read-only lesson whiteboard viewer. Same access list as the
  // backend `lessonWhiteboardForErp` gate — ADMIN / ACCOUNT_MANAGER /
  // METHODIST (mirrors the zoom section).
  whiteboardLesson: (
    <Route
      path={PATHS.whiteboardLesson}
      component={LessonWhiteboard}
      key={PATHS.whiteboardLesson}
    />
  ),
  // Spec 0011 survey constructor. Order matters inside the Switch:
  // results (`/surveys/assignments/:id/results`) must precede the `:id`
  // edit route, `/surveys/new` must precede `:id`, and the list route is
  // `exact` so it never swallows the deeper paths.
  surveyResults: (
    <Route
      path={PATHS.surveyResults}
      component={SurveyResults}
      key={PATHS.surveyResults}
    />
  ),
  surveyNew: (
    <Route
      path={PATHS.surveyNew}
      component={SurveyBuilder}
      key={PATHS.surveyNew}
    />
  ),
  surveyEdit: (
    <Route
      path={PATHS.surveyEdit}
      exact
      component={SurveyBuilder}
      key={PATHS.surveyEdit}
    />
  ),
  surveys: (
    <Route path={PATHS.surveys} exact component={Surveys} key={PATHS.surveys} />
  ),
  // Spec 0018: tabs are query params (`?tab=`), so a single exact route.
  careerCenter: (
    <Route
      path={PATHS.careerCenter}
      exact
      component={CareerCenter}
      key={PATHS.careerCenter}
    />
  ),
  // ТЗ 0024: вкладки живут в `?tab=`, поэтому один exact-роут.
  dofinansowanie: (
    <Route
      path={PATHS.dofinansowanie}
      exact
      component={Dofinansowanie}
      key={PATHS.dofinansowanie}
    />
  ),
  dashboard: (
    <Route
      path={PATHS.dashboard}
      component={AccountManagerDashboard}
      key={PATHS.dashboard}
    />
  ),
  communicator: (
    <Route
      path={PATHS.communicator}
      component={Communicator}
      key={PATHS.communicator}
    />
  ),
};

export const AppRouter = () => {
  const getRoutesByRole = useCallback((role: UserRole | undefined) => {
    switch (role) {
      case UserRole.ADMIN:
        return [...Object.values(ROUTES)];
      case UserRole.ACCOUNTANT:
        return [
          ROUTES.dashboard,
          ROUTES.communicator,
          ROUTES.studentDetail,
          ROUTES.students,
          ROUTES.pandadocProvisioning,
          ROUTES.pandadoc,
          ROUTES.debtors,
          ROUTES.mcpAccess,
          ROUTES.debtorsContributions,
          // Must precede `settings` — `/settings` is non-exact and would
          // shadow /settings/groups/:id/details, i.e. the group card would
          // render the settings list instead (same ordering trap as
          // teacherDetail/teachers below).
          ROUTES.groupDetail,
          ROUTES.settings,
          ROUTES.journal,
          ROUTES.teacherDetail,
          ROUTES.teachers,
          ROUTES.performance,
          ROUTES.zoom,
          ROUTES.whiteboardLesson,
          ROUTES.surveyResults,
          ROUTES.surveyNew,
          ROUTES.surveyEdit,
          ROUTES.surveys,
          ROUTES.careerCenter,
          ROUTES.dofinansowanie,
          ROUTES.financeDashboard,
          ROUTES.fakturowniaInvoice,
          ROUTES.fakturowniaInvoices,
          ROUTES.fakturowniaExpenses,
        ];
      case UserRole.METHODIST:
        // Spec 0011: Methodist is a survey manager role — without these
        // routes the section would be invisible for a pure-Methodist user.
        return [
          ROUTES.zoom,
          ROUTES.whiteboardLesson,
          ROUTES.surveyResults,
          ROUTES.surveyNew,
          ROUTES.surveyEdit,
          ROUTES.surveys,
          ROUTES.careerCenter,
        ];
      case UserRole.SALESMANAGER:
        return [
          ROUTES.dashboard,
          ROUTES.communicator,
          ROUTES.studentDetail,
          ROUTES.students,
          ROUTES.pandadocProvisioning,
          ROUTES.pandadoc,
          ROUTES.debtors,
          ROUTES.mcpAccess,
          ROUTES.debtorsContributions,
          // Must precede `settings` — see the note in ACCOUNTANT above.
          ROUTES.groupDetail,
          ROUTES.settings,
          ROUTES.journal,
          ROUTES.teacherDetail,
          ROUTES.teachers,
          // Read access only; the linking mutations gate by FinanceManager.
          ROUTES.financeDashboard,
          ROUTES.fakturowniaInvoice,
          ROUTES.fakturowniaInvoices,
          // ТЗ 0024: SalesManager ведёт воронку дофинансирования.
          ROUTES.dofinansowanie,
        ];
      case UserRole.HRMANAGER:
        return [
          // Must precede `settings` — see the note in ACCOUNTANT above.
          ROUTES.groupDetail,
          ROUTES.settings,
          ROUTES.journal,
          // Must precede `teachers` — the list route is non-exact and would
          // shadow /teachers/:id/details (same ordering as ACCOUNTANT above).
          ROUTES.teacherDetail,
          ROUTES.teachers,
          ROUTES.performance,
          // Spec 0014: HR files tasks about teachers, so they need the board.
          ROUTES.dashboard,
          // Spec 0018: HR runs the career centre alongside Admin/AM/Methodist.
          ROUTES.careerCenter,
          // ТЗ 0023: HR входит в MANAGEMENT_ROLES, то есть подключить ИИ ему
          // можно и бэкенд его пускает. Без этого роута он не мог ни узнать
          // адрес эндпойнта, ни отозвать своё подключение: страница отдавала
          // 404, хотя доступ был выдан и работал.
          ROUTES.mcpAccess,
        ];
      case UserRole.FINANCIER:
        return [
          ROUTES.dashboard,
          ROUTES.communicator,
          ROUTES.pandadocProvisioning,
          ROUTES.pandadoc,
          ROUTES.debtors,
          ROUTES.mcpAccess,
          ROUTES.debtorsContributions,
          ROUTES.studentDetail,
          ROUTES.students,
          ROUTES.groupDetail,
          ROUTES.teacherDetail,
          ROUTES.finances,
          ROUTES.financeDashboard,
          // ТЗ 0024: только чтение — на запись бэкенд не пускает.
          ROUTES.dofinansowanie,
          ROUTES.financePaymentCalendar,
          ROUTES.financeCashFlow,
          ROUTES.fakturowniaInvoice,
          ROUTES.fakturowniaInvoices,
          ROUTES.fakturowniaExpenses,
        ];
      default:
        return [];
    }
  }, []);
  const userRoles = useReactiveVar(userStateVar).me?.roles;
  const availableRoutes = userRoles
    ? userRoles.map((role) => getRoutesByRole(role as UserRole))
    : [];
  const computedRoutes = availableRoutes.reduce(
    (prev, curr) => [...prev, ...curr],
    [],
  );

  const firstScreenIsStudents = useCallback(() => {
    const allKeys = computedRoutes.map((rout) => rout.key);
    if (allKeys) {
      return allKeys.includes(PATHS.students);
    }
  }, [computedRoutes]);

  return (
    <Suspense fallback={<Splash />}>
      <Switch>
        {firstScreenIsStudents() ? (
          <Redirect exact from={PATHS.main} to={PATHS.students} />
        ) : (
          <Redirect exact from={PATHS.main} to={PATHS.settingsCourses} />
        )}
        <Redirect exact from={PATHS.settings} to={PATHS.settingsCourses} />
        <Redirect exact from={PATHS.teachers} to={PATHS.teachersUsers} />
        <Redirect
          exact
          from={PATHS.debtors}
          to={`${PATHS.contributions}/all`}
        />
        {computedRoutes}
        <Route path="*" render={() => <ErrorPage status={404} />} />
      </Switch>
    </Suspense>
  );
};
